import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ActionSolutionMapping,
  ActiveRiskDetails,
  AddRiskDTO,
  ControlActionMapping,
  ControlActionMappingDTO,
  ControlAssetType,
  InactiveRisk,
  Risk,
  RiskAction,
  RiskActionDTO,
  RiskActionRemediationSolution,
  RiskActionToolType,
  RiskBudget,
  RiskContractualComplianceCoverage,
  RiskControlOptions,
  RiskCoverage,
  RiskDetails,
  RiskLevel,
  RiskMitigationStrategyType,
  RiskProjectedScore,
  RiskLocalLawsComplianceCoverage,
  RiskSolutionCommercial,
  RiskStatusName,
  SavePlanSimulationChangeDTO,
  ActiveRisk,
  WarningBox,
  Control,
} from '../interfaces';
import { formatSeverity, calculateImplementationTimeline } from '../helpers';
import { BusinessClockService } from './business-clock.service';

@Injectable({ providedIn: 'root' })
export class RisksService {
  private _url = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private businessClockService: BusinessClockService,
  ) {}

  public fetchControlsForRisk(riskId: string): Observable<Control[]> {
    return this.http
      .get<
        Control[]
      >(`${this._url}/CMDISOSecurityProgramControls/GetAllControlsForRisk`, { params: { riskId } })
      .pipe(
        map((controls: Control[]) => {
          return controls.map((control) => ({
            ...control,
            severity: formatSeverity(control.priority),
            implementationTimeline: calculateImplementationTimeline(
              control.dueBy,
              this.businessClockService.nowUtc(),
            ),
          }));
        }),
      );
  }

  public fetchActiveRisks(): Observable<{
    risks: ActiveRisk[];
    warningBoxes: WarningBox;
  }> {
    return this.http.get<{ risks: ActiveRisk[]; warningBoxes: WarningBox }>(
      `${environment.baseUrl}/CMDISORiskInOrg/Dashboard`,
    );
  }

  public markNewRisksAsReviewed(riskIds: string[], isReviewed: boolean = true): Observable<void> {
    return this.http.post<void>(`${environment.baseUrl}/CMDISORiskInOrg/ReviewBatch`, {
      riskIds,
      isReviewed,
    });
  }

  public fetchActiveRiskDetails(riskId: string): Observable<ActiveRiskDetails> {
    return this.http.get<ActiveRiskDetails>(`${this._url}/CMDISORiskInOrg/${riskId}/Details`).pipe(
      map((details) => {
        return {
          ...details,
          references: [...details.references].sort((a, b) =>
            a.isBold === b.isBold ? 0 : a.isBold ? -1 : 1,
          ),
        };
      }),
    );
  }

  public fetchAllInactiveRisks(): Observable<InactiveRisk[]> {
    return this.http.get<InactiveRisk[]>(`${this._url}/CMDISORiskTemplates/GetInactives`);
  }

  public fetchRiskDetails(riskId: string, planId: string): Observable<RiskDetails> {
    return this.http.get<RiskDetails>(`${this._url}/CMDISORiskInOrg/GetManualRiskForEdit`, {
      params: {
        riskId,
        planId,
      },
    });
  }

  public fetchRiskControls(riskId: string): Observable<RiskControlOptions> {
    return this.http.get<RiskControlOptions>(
      `${this._url}/CMDISORiskTemplates/GetRiskControls/${riskId}`,
    );
  }

  public fetchAssetTypes(controlIds: string[]): Observable<ControlAssetType> {
    return this.http.post<ControlAssetType>(
      `${this._url}/CMDISOControlTemplates/GetControlsAssetInfos`,
      controlIds,
    );
  }

  public fetchRiskLevels(riskId: string): Observable<{
    impact: RiskLevel;
    likelihood: RiskLevel;
  }> {
    return this.http.get<{
      impact: RiskLevel;
      likelihood: RiskLevel;
    }>(`${this._url}/CMDISORiskTemplates/GetRiskLevels/${riskId}`);
  }

  public fetchRiskActions(riskActionDTO: RiskActionDTO): Observable<RiskAction[]> {
    return this.http
      .post<ControlActionMappingDTO>(
        `${this._url}/CMDISOControlTemplates/LoadControlActionCandidates`,
        riskActionDTO,
      )
      .pipe(
        map((res) => {
          const riskActions = res.controls
            .map((control: ControlActionMapping) => {
              return control.actions.map((action: ActionSolutionMapping) => {
                const solutionsCatalog = action.solutionsCatalog.map(
                  (solution: {
                    commercial: RiskSolutionCommercial;
                    solutionId: string;
                    solutionName: string;
                    solutionType: number;
                    vendorName: string;
                  }) => {
                    return {
                      ...solution,
                      commercial: {
                        ...solution.commercial,
                        isAcquired:
                          (solution.commercial.licenseEndUtc &&
                            new Date(solution.commercial.licenseEndUtc) <
                              new Date(solution.commercial.planEndDate)) ||
                          (solution.commercial.licenseEndUtc && !solution.commercial.planEndDate),
                        isLicenseExpired:
                          solution.commercial.licenseEndUtc &&
                          new Date(solution.commercial.licenseEndUtc) < new Date(res.planEndDate),
                      },
                    };
                  },
                );
                return {
                  id: action.actionId,
                  name: action.actionName,
                  controlId: control.controlId,
                  controlName: control.controlName,
                  controlCount: action.controlsInPlanCount ?? 0,
                  remediationSolution: !action.isUnified
                    ? RiskActionRemediationSolution.MULTIPLE
                    : RiskActionRemediationSolution.UNIFIED,
                  assetTypes: action.assetTypeSelections.map(
                    (asset: ActionSolutionMapping['assetTypeSelections'][number]) => {
                      return {
                        ...asset,
                        id: asset.assetTypeId,
                      };
                    },
                  ),
                  allowedAssetTypeIds: action.allowedAssetTypeIds ?? [],
                  outOfScopeAssetTypeIds: action.outOfScopeAssetTypeIds ?? [],
                  requiresUpdate: action.requiresUpdate,
                  existingPlanMappings: action.existingPlanMappings ?? [],
                  solutionsCatalog,
                  toolType: RiskActionToolType.TOOL,
                  toolOptions: solutionsCatalog.filter(
                    (solution: { solutionType: number }) => solution.solutionType === 1,
                  ),
                  serviceOptions: solutionsCatalog.filter(
                    (solution: { solutionType: number }) => solution.solutionType === 2,
                  ),
                  tool: {} as RiskAction['tool'],
                  service: {} as RiskAction['service'],
                } as unknown as RiskAction;
              });
            })
            .flat();
          return riskActions;
        }),
      );
  }

  public fetchRiskActionsByRiskId(riskId: string): Observable<RiskAction[]> {
    // Use the same mock data as fetchRiskActions
    return this.fetchRiskActions({
      planId: 'eaa62d5c-7a28-4a2f-91ae-b24e0ca29e8b',
      controlAssetSelection: [
        {
          controlId: '11111111-aaaa-bbbb-cccc-000000000001',
          assetTypeIds: [
            '33333333-aaaa-bbbb-cccc-0000000000a1',
            '33333333-aaaa-bbbb-cccc-0000000000a2',
          ],
        },
        {
          controlId: '11111111-aaaa-bbbb-cccc-000000000002',
          assetTypeIds: ['33333333-aaaa-bbbb-cccc-0000000000a1'],
        },
      ],
    });
  }

  getQuarterlyPeriods(
    startDateStr: string,
    quartersLength: number,
    targetDate: string,
  ): { name: string; isSelected: boolean }[] {
    const result: { name: string; isSelected: boolean }[] = [];
    const startDate = new Date(startDateStr);
    const now = this.businessClockService.nowUtc();

    // Calculate the starting quarter and year
    let startYear = startDate.getFullYear();
    let startMonth = startDate.getMonth();
    let startQuarter = Math.floor(startMonth / 3) + 1;

    // Build all quarters up to the desired length
    for (let i = 0; i < quartersLength; i++) {
      let quarter = ((startQuarter - 1 + i) % 4) + 1;
      let year = startYear + Math.floor((startQuarter - 1 + i) / 4);

      const quarterLastDay = new Date(year, quarter * 3, 0);
      const quarterEnd = new Date(
        quarterLastDay.getFullYear(),
        quarterLastDay.getMonth(),
        quarterLastDay.getDate(),
        23,
        59,
        59,
        999,
      );

      // Skip fully past quarters (compare through end of last calendar day, not midnight on that day)
      if (quarterEnd >= now) {
        result.push({
          name: `Q${quarter} ${year}`,
          isSelected: `Q${quarter} ${year}` === targetDate,
        });
      }
    }

    return result;
  }

  fetchRiskSimulation(
    planId: string,
    planStartDate: string,
    quarterLength: number,
  ): Observable<{
    risks: Risk[];
    addManualRiskAvailability: {
      allRisksExist: boolean;
      messageId: string | null;
    };
  }> {
    return this.http
      .get<{
        risks: Risk[];
        addManualRiskAvailability: {
          allRisksExist: boolean;
          messageId: string | null;
        };
      }>(`${environment.baseUrl}/CMDISORiskInOrg/DetectedRisks`, {
        params: { planId },
      })
      .pipe(
        map((res) => {
          return {
            addManualRiskAvailability: res.addManualRiskAvailability,
            risks: res.risks.map((risk) => ({
              ...risk,
              mitigationStrategies: risk.mitigationStrategies.map((strategy) => {
                return {
                  ...strategy,
                  options: this.getQuarterlyPeriods(
                    planStartDate,
                    quarterLength,
                    strategy.targetDate,
                  ),
                };
              }),
            })),
          };
        }),
        map((res) => {
          return {
            risks: res.risks.sort((a, b) => a.riskName.localeCompare(b.riskName)),
            addManualRiskAvailability: res.addManualRiskAvailability,
          };
        }),
      );
  }

  changeRiskStrategy(strategy: RiskMitigationStrategyType, id: string): Observable<string> {
    return of('success');
    // return this.http.put<void>(
    //   `${environment.baseUrl}/CMDISOSecurityPlan/ChangeRiskStrategy`,
    //   {
    //     strategy,
    //     id,
    //   },
    // );
  }

  changeRiskStrategyStatus(
    planId: string,
    status: RiskStatusName,
    riskId: string,
    statusComment?: string,
  ): Observable<void> {
    return this.http.post<void>(`${environment.baseUrl}/CMDISORiskInOrg/SavePlanSimulationChange`, {
      planId: planId,
      riskInPlanId: riskId,
      statusChanged: true,
      rimsChanged: false,
      targetDateChanged: false,
      solutionMappingChanged: false,
      newStatus: status,
      newStatusComment: statusComment,
    });
  }

  fetchRiskSimulationProjectedScore(planId: string): Observable<RiskProjectedScore> {
    return this.http.get<RiskProjectedScore>(
      `${environment.baseUrl}/CMDISOSecurityProgram/GetProjectedSystemScore`,
      {
        params: { planId },
      },
    );
  }

  fetchRiskCoverage(planId: string): Observable<RiskCoverage> {
    return this.http.get<RiskCoverage>(
      `${environment.baseUrl}/CMDISORiskInOrg/CalculateRiskCoverageForPlan`,
      {
        params: { planId },
      },
    );
  }

  fetchRemainingBudget(planId: string): Observable<RiskBudget> {
    return this.http.get<RiskBudget>(
      `${environment.baseUrl}/CMDISOSecurityPlan/GetRemainingBudget`,
      {
        params: { planId },
      },
    );
  }

  fetchContractualCompliance(planId: string): Observable<RiskContractualComplianceCoverage> {
    return this.http.get<RiskContractualComplianceCoverage>(
      `${environment.baseUrl}/CMDISOSecurityProgram/CalculateContractualComplianceForPlan`,
      {
        params: { planId },
      },
    );
  }

  fetchRegulatoryCompliance(planId: string): Observable<RiskContractualComplianceCoverage> {
    return this.http.get<RiskContractualComplianceCoverage>(
      `${environment.baseUrl}/CMDISOSecurityProgram/CalculateRegulatoryCoverage`,
      {
        params: { planId },
      },
    );
  }

  fetchLocalLawsCompliance(planId: string): Observable<RiskLocalLawsComplianceCoverage> {
    return this.http.get<RiskLocalLawsComplianceCoverage>(
      `${environment.baseUrl}/CMDISOSecurityProgram/GetSensitiveDataProtectionCoverageForPlan`,
      {
        params: { planId },
      },
    );
  }

  addRisk(riskDto: AddRiskDTO): Observable<{ rimsId: string; riskInOrgId: string }> {
    return this.http.post<{ rimsId: string; riskInOrgId: string }>(
      `${environment.baseUrl}/CMDISORiskInOrg/SaveManual`,
      riskDto,
    );
  }

  savePlanSimulationChange(
    savePlanSimulationChangeDto: SavePlanSimulationChangeDTO,
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.baseUrl}/CMDISORiskInOrg/SavePlanSimulationChange`,
      savePlanSimulationChangeDto,
    );
  }
}
